import { fg } from '@atlaskit/platform-feature-flags/fg';
import { isContentTypeEnabledInCurrentEnv } from '@atlassian/content-types-rollout';
import { type NetworkCallbacks } from '@atlassian/search-client/persist-refresh';
import { cachedRecentActivitiesQuery } from '@atlassian/search-client/recent-activities';
import {
	type ActivityItemEdge,
	is1PActivity,
	isAssetsActivity,
	isDocumentActivity,
	isGraphActivity,
	isLoomActivity,
	isGraphPullRequestActivity,
	isGraphWorkItemActivity,
	isRemoteLinkActivity,
	type QuickFindRecentActivitiesQueryVariables,
} from '@atlassian/search-client/recent-activities/types';

import { PrimaryProductKeys } from '../../../../common/constants/primary-product-keys';
import { ProductKeys } from '../../../../common/constants/product-keys';
import type { ThirdPartyConfigsBootstrap } from '../../../../common/constants/schemas/3p-config';
import { shouldShowSearchAppRefresh } from '../../../../common/utils/experiments';
import { isEligibleFor3PClickIngestion } from '../../../../common/utils/is-eligible-for-3p-click-ingestion';

/**
 * Returns whether Loom recents should be included.
 *
 * Loom-hosted Quick Find remains controlled by its own rollout gate. Other hosts include Loom
 * recents only for the products that received the shipped cohort and for users with Loom access.
 */
const shouldIncludeLoomRecents = (
	availableProducts?: ProductKeys[],
	primaryProduct?: string,
): boolean => {
	if (primaryProduct === PrimaryProductKeys.Loom) {
		return fg('loom-rovo-search');
	}

	if (
		primaryProduct !== PrimaryProductKeys.Confluence &&
		primaryProduct !== PrimaryProductKeys.Atlas &&
		primaryProduct !== PrimaryProductKeys.RovoExtension
	) {
		return false;
	}

	return availableProducts?.includes(ProductKeys.Loom) ?? false;
};

export const INCLUDED_PRODUCTS: string[] = ['confluence', 'jira', 'townsquare', 'graph'];

export const INCLUDED_OBJECT_TYPES: string[] = [
	'page',
	'blogpost',
	'whiteboard',
	'issue',
	'project',
	'goal',
	'content',
	'document',
	'remote-link',
	'pull-request',
	'work-item',
];

/**
 * Event types to be included in the recent activities query.
 * Based upon the AVI actions for each product: https://bitbucket.org/atlassian/flight-deck/src/7bab054550927c3c023f2eb783ce9d2ca6279bfc/packages/integrations/worklens/contrib/
 */
const INCLUDED_EVENT_TYPES = [
	'assigned',
	'unassigned',
	'viewed',
	'updated',
	'created',
	'liked',
	'transitioned',
	'published',
	'edited',
	'commented',
	'sl-viewed',
	'sl-clicked',
	'shared',
	'exported',
	'deleted',
	'started',
	'snapshotted',
	'trashed',
	'reaction',
];

/**
 * Returns the list of products to be included in the recent activities query.
 */
const getIncludedProducts = (
	availableProducts?: ProductKeys[],
	primaryProduct?: string,
): string[] => {
	return [
		...INCLUDED_PRODUCTS,
		...(fg('assets_rovo_search_all') ? ['assets'] : []),
		...(shouldIncludeLoomRecents(availableProducts, primaryProduct) ? [ProductKeys.Loom] : []),
	];
};

const getIncludedProductsBasedOnProduct = (
	includedProducts: string[],
	product?: string,
): string[] => {
	/**
	 * Decision on Experiment (https://hello.atlassian.net/wiki/spaces/CentAI/pages/5652280226)
	 * Remove Jira from QF Recents to have better relevance and CTR.
	 */
	if (product === PrimaryProductKeys.Confluence) {
		return includedProducts.filter((product) => product !== PrimaryProductKeys.Jira);
	}

	if (product === PrimaryProductKeys.Jira) {
		return includedProducts.filter((p) => p !== PrimaryProductKeys.Confluence);
	}

	if (product === PrimaryProductKeys.Loom && fg('loom-rovo-search')) {
		return includedProducts.filter((p) => p === ProductKeys.Loom || p === 'graph');
	}

	return includedProducts;
};

const getFilteredEventTypes = (thirdPartyConfigs?: ThirdPartyConfigsBootstrap): string[] => {
	if (thirdPartyConfigs && isEligibleFor3PClickIngestion(thirdPartyConfigs)) {
		return [...INCLUDED_EVENT_TYPES, 'clicked'];
	}

	return INCLUDED_EVENT_TYPES;
};

const getFilteredProducts = (
	primaryProduct?: string,
	availableProducts?: ProductKeys[],
): string[] => {
	// Get products that are available to this Quick Find host.
	const includedProducts = getIncludedProducts(availableProducts, primaryProduct);

	// Further filter products based on primary product
	const filteredProductsBasedOnProduct = getIncludedProductsBasedOnProduct(
		includedProducts,
		primaryProduct,
	);

	return filteredProductsBasedOnProduct;
};

type FetchRecentActivitiesOptions = {
	includeSearchAppMetadata?: boolean;
};

export const fetchRecentActivities: (
	cloudId: string,
	aggAbsoluteUrl: string | undefined,
	after?: string,
	thirdPartyConfigs?: ThirdPartyConfigsBootstrap,
	headers?: Record<string, string>,
	primaryProduct?: string,
	networkCallbacks?: NetworkCallbacks,
	availableProducts?: ProductKeys[],
	options?: FetchRecentActivitiesOptions,
) => Promise<
	{
		id: string;
		title: string;
		data: ActivityItemEdge;
		type: 'recent-activity';
		activityTimestamp: string;
	}[]
> = async (
	cloudId: string,
	aggAbsoluteUrl: string | undefined,
	after: string = '',
	thirdPartyConfigs?: ThirdPartyConfigsBootstrap,
	headers?: Record<string, string>,
	primaryProduct?: string,
	networkCallbacks?: NetworkCallbacks,
	availableProducts?: ProductKeys[],
	options?: FetchRecentActivitiesOptions,
) => {
	const filteredProducts = getFilteredProducts(primaryProduct, availableProducts);

	const variables: QuickFindRecentActivitiesQueryVariables = {
		filteredProducts: Array.from(new Set(filteredProducts)),
		filteredObjectTypes: Array.from(
			new Set([
				...INCLUDED_OBJECT_TYPES,
				// Confluence Slides in Recents are gated behind the environment content-type rollout.
				...(isContentTypeEnabledInCurrentEnv('slide') ? ['slide'] : []),
				...(fg('assets_rovo_search_all') ? ['object', 'object_type', 'schema'] : []),
				...(shouldIncludeLoomRecents(availableProducts, primaryProduct) ? ['video'] : []),
			]),
		),
		filteredRootContainerIds: [
			`ari:cloud:platform::site/${cloudId}`,
			`ari:cloud:townsquare::site/${cloudId}`,
			...(shouldIncludeLoomRecents(availableProducts, primaryProduct)
				? [`ari:cloud:loom::site/${cloudId}`]
				: []),
		],
		itemsLimit: 200,
		after,
		includeJiraIssueMetadata: true,
		...(options?.includeSearchAppMetadata && shouldShowSearchAppRefresh(primaryProduct)
			? { includeSearchAppMetadata: true }
			: {}),
		includeLoomVideoMetadata: fg('loom-rovo-search'),
		filteredEventTypes: [
			...getFilteredEventTypes(thirdPartyConfigs),
			...(fg('rovo_quickfind_linksent_event_type') ? ['linksent'] : []),
			...(fg('rovo_quickfind_linkreceived_event_type') ? ['linkreceived'] : []),
		],
	};

	const response = await cachedRecentActivitiesQuery({
		variables,
		aggAbsoluteUrl,
		headers,
		onlyRefreshIfExpired: true,
		returnExpired: false,
		networkCallbacks,
	});

	const edges = response.data?.activity?.myActivity?.all?.edges || [];
	return edges.map(mapEssentialData);
};

export const mapEssentialData: (result: ActivityItemEdge) => {
	id: string;
	title: string;
	data: ActivityItemEdge;
	type: 'recent-activity';
	activityTimestamp: string;
} = (result: ActivityItemEdge) => {
	const product = result.node.object.product;
	let title = '';
	const timestamp = result.node?.event?.timestamp;

	if (is1PActivity(result.node.object)) {
		if (product === 'confluence') {
			title = result.node.object.data?.title || '';
		} else if (product === 'jira') {
			const key = result.node.object.data?.key;
			const summary = result.node.object.data?.fieldsById?.edges?.[0]?.node?.text;
			title = key && summary ? `${key}: ${summary}` : '';
		} else if (product === 'townsquare') {
			title = result.node.object.data?.name || '';
		}
	} else if (
		isGraphActivity(result.node.object) &&
		(isDocumentActivity(result.node.object) ||
			isRemoteLinkActivity(result.node.object) ||
			isGraphPullRequestActivity(result.node.object) ||
			isGraphWorkItemActivity(result.node.object))
	) {
		title = result.node.object.data?.displayName || '';
	} else if (isLoomActivity(result.node.object)) {
		title = result.node.object.data?.name || '';
	} else if (isAssetsActivity(result.node.object)) {
		title = fg('cdm_ga')
			? result.node.object.data?.assetsDisplayName || ''
			: result.node.object.data?.assetsLabel || '';
	} else {
		title = '';
	}

	return {
		id: result.node.object.id,
		title,
		data: result,
		type: 'recent-activity' as const,
		activityTimestamp: timestamp,
	};
};
